import doNothing from '@opsbase/lib-general/utils/doNothing';
import i18n from '@opsbase/lib-i18n';
import IssueRequiredField, { issueRequiredFieldLabel, } from '@opsbase/lib-issue/IssueRequiredField/IssueRequiredField';
import IssueStatus from '@opsbase/lib-issue/IssueStatus';
import Pages from '@opsbase/lib-pages';
import Palette from '@opsbase/lib-palette';
import { SPTType } from '@opsbase/lib-spt';
import useSelf from '@opsbase/react-ctrl-self/useSelf/useSelf';
import useConversationUpdate from '@opsbase/react-gql-hooks/conversation/useConversationUpdate';
import useIssueDetailsReportURL from '@opsbase/react-gql-hooks/issue/useIssueDetailsReportURL';
import useIssueFindById from '@opsbase/react-gql-hooks/issue/useIssueFindById';
import useIssueUpdate from '@opsbase/react-gql-hooks/issue/useIssueUpdate';
import useCallforward from '@opsbase/react-hooks/useCallforward';
import { useCss } from '@opsbase/web-css';
import Dialog from '@opsbase/web-dialogs/Dialog';
import DialogSize from '@opsbase/web-dialogs/Dialog/DialogSize';
import ReportDownloadIcon from '@opsbase/web-icons/icons/ReportDownloadIcon';
import ReportViewIcon from '@opsbase/web-icons/icons/ReportViewIcon';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import useNav from '@opsbase/web-nav/useNav';
import useParams from '@opsbase/web-nav/useParams';
import useNotificationActions from '@opsbase/web-notification/useNotificationActions/useNotificationActions';
import PickStaffDialog from '@opsbase/web-opform-perform/PickStaffDialog/PickStaffDialog';
import Tab from '@opsbase/web-tabs/Tab/Tab';
import Tabs from '@opsbase/web-tabs/Tabs/Tabs';
import Button from '@opsbase/web-ui/Button';
import Spinner from '@opsbase/web-ui/Spinner';
import Text from '@opsbase/web-ui/Text';
import React, { useCallback, useEffect, useState } from 'react';
import IssueEditCausesPane from './IssueEditCausesPane';
import IssueEditConversationPane from './IssueEditConversationPane';
import IssueEditMainPane from './IssueEditMainPane';
const makeMyClasses = () => ({
    leftPane: {
        minWidth: '40%',
    },
    rightPane: {
        minWidth: '60%',
    },
    causesAndActionsPane: {
        border: `1px solid ${Palette.Platinum.main}`,
        backgroundColor: Palette.WhiteSmoke.main,
    },
    conversationPane: {
        '& .ConversationView': {
            borderRadius: 0,
            borderColor: Palette.Platinum.main,
            backgroundColor: Palette.WhiteSmoke.main,
        },
        '& .PhotoTextInput, & .Callout': {
            backgroundColor: Palette.White.main,
        },
    },
});
export function IssueEditDialog() {
    const { id } = useParams();
    const { raise } = useNotificationActions();
    const updateIssue = useIssueUpdate();
    const updateConversation = useConversationUpdate();
    const [hasAttemptedIssueUpdate, setHasAttemptedIssueUpdate] = useState(false);
    const [sideTab, setSideTab] = useState('conversation');
    const { loading, data: issue } = useIssueFindById({ variables: { id } });
    const myCss = useCss(makeMyClasses);
    const [showStaffListDialog, setShowStaffListDialog] = useState(false);
    const [patch, setPatch] = useState({});
    const self = useSelf();
    const { goBack } = useNav();
    const handleClose = useCallback(() => {
        setShowStaffListDialog(false);
    }, []);
    const handleSelectStaff = useCallback(staff => {
        setShowStaffListDialog(false);
        void raise(updateIssue({
            id,
            patch: {
                ...patch,
                updatedBy: { id: staff.id },
                closedBy: patch['status'] === IssueStatus.CLOSED ? { id: staff.id } : {},
            },
        }));
        patch['status'] === IssueStatus.CLOSED && goBack();
    }, [raise, updateIssue, id, patch, goBack]);
    const handleChange = useCallback(async (p) => {
        if (!self.isStaff) {
            setShowStaffListDialog(true);
            setPatch(p);
        }
        else {
            void raise(updateIssue({
                id,
                patch: p,
            }));
        }
    }, [raise, id, updateIssue, self]);
    useEffect(() => {
        if (!hasAttemptedIssueUpdate && issue && !issue.conversation) {
            setHasAttemptedIssueUpdate(true);
            updateIssue({ id: issue.id, patch: { title: issue.title } }).catch(doNothing);
        }
        if (!hasAttemptedIssueUpdate && (issue === null || issue === void 0 ? void 0 : issue.conversation) && !issue.conversation.relatedIssue) {
            setHasAttemptedIssueUpdate(true);
            updateConversation({
                id: issue.conversation.id,
                patch: { relatedIssue: { id: issue.id } },
            }).catch(doNothing);
        }
    }, [hasAttemptedIssueUpdate, issue, updateConversation, updateIssue]);
    const handleChangeTitle = useCallback(title => handleChange({ title }), [handleChange]);
    const handleChangeSideTab = useCallback((_, v) => setSideTab(v), []);
    return (React.createElement(Dialog, { size: DialogSize.X1000_YFULL, white: true, editableTitle: true, title: issue === null || issue === void 0 ? void 0 : issue.title, onChangeTitle: handleChangeTitle, loading: loading, padded: false, buttonsLeft: issue && React.createElement(ReportButtons, { issue: issue }), buttonsRight: issue && React.createElement(StatusButtons, { issue: issue, onChange: handleChange }) },
        React.createElement(Spinner, { busy: loading }, () => (React.createElement(Horizontal, { stretch: true },
            React.createElement(Pane, { padding: true, classes: { root: myCss.leftPane }, scroll: true },
                React.createElement(IssueEditMainPane, { issue: issue, onChange: handleChange })),
            React.createElement(Vertical, { padding: true, classes: { root: myCss.rightPane } },
                React.createElement(Tabs, { value: sideTab, onChange: handleChangeSideTab, indicatorColor: "primary", textColor: "primary" },
                    React.createElement(Tab, { label: i18n.t('conversation'), value: 'conversation' }),
                    React.createElement(Tab, { label: i18n.t('causes-actions'), value: 'causes' })),
                sideTab === 'conversation' && (React.createElement(IssueEditConversationPane, { issue: issue })),
                sideTab === 'causes' && (React.createElement(IssueEditCausesPane, { issue: issue, onChange: handleChange })))))),
        showStaffListDialog && (React.createElement(PickStaffDialog, { selfieRequired: false, onSelectStaff: handleSelectStaff, onClose: handleClose }))));
}
export default IssueEditDialog;
function StatusButtons({ issue, onChange }) {
    const self = useSelf();
    const requirements = self === null || self === void 0 ? void 0 : self.settings.getIssueTypeRequirements(issue.type).map(f => {
        var _a, _b, _c;
        switch (f) {
            case IssueRequiredField.CAUSES:
                return !!((_a = issue.causes) === null || _a === void 0 ? void 0 : _a.length)
                    ? undefined
                    : issueRequiredFieldLabel(IssueRequiredField.CAUSES);
            case IssueRequiredField.CORRECTIVE_ACTIONS:
                return !!((_b = issue.correctiveActions) === null || _b === void 0 ? void 0 : _b.length)
                    ? undefined
                    : issueRequiredFieldLabel(IssueRequiredField.CORRECTIVE_ACTIONS);
            case IssueRequiredField.PREVENTATIVE_ACTIONS:
                return !!((_c = issue.preventativeActions) === null || _c === void 0 ? void 0 : _c.length)
                    ? undefined
                    : issueRequiredFieldLabel(IssueRequiredField.PREVENTATIVE_ACTIONS);
        }
    }).filter(v => !!v);
    const handleSetStatus = useCallforward(status => () => {
        onChange({ status }).catch(doNothing);
    }, [onChange]);
    return (issue && (React.createElement(Vertical, { gutter: true },
        React.createElement(Horizontal, { gutter: true, justify: "flex-end" },
            (issue === null || issue === void 0 ? void 0 : issue.status) === IssueStatus.CLOSED && (React.createElement(Button, { disabled: issue.assignedToSPTType === SPTType.STAFF &&
                    self.isTeamboard &&
                    !self.showStaffIssues, color: Palette.error, onClick: handleSetStatus(IssueStatus.IN_PROGRESS), label: i18n.t('reopen') })),
            (issue === null || issue === void 0 ? void 0 : issue.status) !== IssueStatus.CLOSED && (React.createElement(Button, { disabled: !!requirements.length ||
                    (issue.assignedToSPTType === SPTType.STAFF &&
                        self.isTeamboard &&
                        !self.showStaffIssues), color: Palette.positive, onClick: handleSetStatus(IssueStatus.CLOSED), label: i18n.t('mark-closed') })),
            (issue === null || issue === void 0 ? void 0 : issue.status) === IssueStatus.NEW && (React.createElement(Button, { disabled: issue.assignedToSPTType === SPTType.STAFF &&
                    self.isTeamboard &&
                    !self.showStaffIssues, color: Palette.positive, onClick: handleSetStatus(IssueStatus.IN_PROGRESS), label: i18n.t('mark-started') })),
            (issue === null || issue === void 0 ? void 0 : issue.status) === IssueStatus.IN_PROGRESS && (React.createElement(Button, { disabled: issue.assignedToSPTType === SPTType.STAFF &&
                    self.isTeamboard &&
                    !self.showStaffIssues, color: Palette.error, onClick: handleSetStatus(IssueStatus.NEW), label: i18n.t('mark-not-started') }))),
        (requirements === null || requirements === void 0 ? void 0 : requirements.length) && (React.createElement(Pane, null,
            React.createElement(Text, { color: "error" }, requirements.join(', ') + ' ' + i18n.t('are-required')))))));
}
function ReportButtons({ issue }) {
    const { pathname } = useNav();
    const { data: issueDetailsReportUrl } = useIssueDetailsReportURL({
        variables: { id: issue === null || issue === void 0 ? void 0 : issue.id },
    });
    const handleClickViewReport = useCallback(() => {
        window.open(issueDetailsReportUrl, '_blank');
    }, [issueDetailsReportUrl]);
    return (React.createElement(Horizontal, { shrink: true, gutter: true, justify: 'flex-start' },
        React.createElement(Button, { icon: React.createElement(ReportViewIcon, { size: 16 }), label: i18n.t('view-details-report'), color: Palette.positive, onClick: handleClickViewReport }),
        React.createElement(Button, { icon: React.createElement(ReportDownloadIcon, { size: 16 }), label: i18n.t('download-details-report'), link: Pages.ISSUE_GENERATE_DETAILS_REPORT.replace(':id', issue.id).replace('*', pathname) })));
}
